import { matchPath } from 'react-router-dom';
import { PATH } from '@/router/PATH';
import { MANAGE_FEATURES } from '@/router/manageFeatures';

export type SecuredNavigationItem = {
    badge?: string;
    href: string;
    icon: string;
    labelKey: string;
    matchPaths?: string[];
    slug: string;
};

export type SecuredNavigationGroup = {
    icon: string;
    items: SecuredNavigationItem[];
    labelKey: string;
    slug: string;
};

/**
 * The admin app sidebar: Dashboard first, then the built pages (Transactions,
 * Shifts), then the management features (stub pages). One item per top-level
 * route, mirroring the bos-app convention. Only a BranchAdmin reaches the
 * layout-wrapped routes, so this nav is admin-only in effect.
 */
export const SECURED_NAVIGATION_ITEMS: SecuredNavigationItem[] = [
    {
        href: PATH.MANAGE,
        icon: 'solar:widget-5-bold-duotone',
        labelKey: 'layout.navigation.dashboard',
        matchPaths: [PATH.MANAGE],
        slug: 'dashboard',
    },
    {
        href: PATH.TRANSACTIONS,
        icon: 'solar:bill-list-bold-duotone',
        labelKey: 'layout.navigation.transactions',
        matchPaths: [PATH.TRANSACTIONS],
        slug: 'transactions',
    },
    {
        href: PATH.SHIFTS,
        icon: 'solar:clock-circle-bold-duotone',
        labelKey: 'layout.navigation.shifts',
        matchPaths: [PATH.SHIFTS],
        slug: 'shifts',
    },
    ...MANAGE_FEATURES.map((feature) => ({
        href: feature.path,
        icon: feature.icon,
        labelKey: `layout.navigation.manage.${feature.slug}`,
        matchPaths: [feature.path],
        slug: feature.slug,
    })),
];

export const SECURED_NAVIGATION_GROUPS: SecuredNavigationGroup[] = [];

export const isNavigationItemActive = (
    item: SecuredNavigationItem,
    pathname: string,
): boolean =>
    (item.matchPaths ?? [item.href]).some(
        (matchPath) =>
            pathname === matchPath ||
            (matchPath !== PATH.HOME && pathname.startsWith(`${matchPath}/`)),
    );

const ALL_NAVIGATION_ITEMS: SecuredNavigationItem[] = [
    ...SECURED_NAVIGATION_ITEMS,
    ...SECURED_NAVIGATION_GROUPS.flatMap((group) => group.items),
];

type SubpageNavigation = {
    /** Route pattern (a `PATH` constant; may carry `:params`). */
    path: string;
    titleKey: string;
    /** When set, the navbar back button always shows on this page and goes
     *  here — a plain navigation, regardless of how the page was reached.
     *  Nothing is saved on the way out. */
    backTarget?: string;
};

/**
 * Standalone sub-pages that live under a nav item's path get their own navbar
 * title here, checked before the prefix match so they read as their own page
 * rather than the parent section. Patterns are matched with react-router's
 * `matchPath`, so parameterised routes resolve too. Empty for now — add an
 * entry when a sale-point flow gains its own sub-page.
 */
const SUBPAGE_NAVIGATION: SubpageNavigation[] = [];

const findSubpageNavigation = (
    pathname: string,
): SubpageNavigation | undefined =>
    SUBPAGE_NAVIGATION.find(
        (subpage) => matchPath(subpage.path, pathname) !== null,
    );

export const getNavigationTitle = (
    pathname: string,
    t: (key: string) => string,
): string => {
    const subpage = findSubpageNavigation(pathname);
    if (subpage) {
        return t(subpage.titleKey);
    }

    const matchedItem = ALL_NAVIGATION_ITEMS.find((item) =>
        isNavigationItemActive(item, pathname),
    );

    return matchedItem
        ? t(matchedItem.labelKey)
        : t('layout.navigation.adminWorkspace');
};

/**
 * Fixed back target for the current page, or null when the navbar back
 * button should fall back to its default history behaviour.
 */
export const getNavigationBackTarget = (pathname: string): string | null =>
    findSubpageNavigation(pathname)?.backTarget ?? null;
